import { useMemo, useRef, useState } from 'react';
import { geoAlbersUsa, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import topology from './data/usa_states.json';

// Mapping from FIPS codes (from the TopoJSON) to state abbreviations.
const fipsToName: Record<string, string> = {
  '01': 'Alabama (AL)',
  '02': 'Alaska (AK)',
  '04': 'Arizona (AZ)',
  '05': 'Arkansas (AR)',
  '06': 'California (CA)',
  '08': 'Colorado (CO)',
  '09': 'Connecticut (CT)',
  '10': 'Delaware (DE)',
  '11': 'DC (DC)',
  '12': 'Florida (FL)',
  '13': 'Georgia (GA)',
  '15': 'Hawaii (HI)',
  '16': 'Idaho (ID)',
  '17': 'Illinois (IL)',
  '18': 'Indiana (IN)',
  '19': 'Iowa (IA)',
  '20': 'Kansas (KS)',
  '21': 'Kentucky (KY)',
  '22': 'Louisiana (LA)',
  '23': 'Maine (ME)',
  '24': 'Maryland (MD)',
  '25': 'Massachusetts (MA)',
  '26': 'Michigan (MI)',
  '27': 'Minnesota (MN)',
  '28': 'Mississippi (MS)',
  '29': 'Missouri (MO)',
  '30': 'Montana (MT)',
  '31': 'Nebraska (NE)',
  '32': 'Nevada (NV)',
  '33': 'New Hampshire (NH)',
  '34': 'New Jersey (NJ)',
  '35': 'New Mexico (NM)',
  '36': 'New York (NY)',
  '37': 'North Carolina (NC)',
  '38': 'North Dakota (ND)',
  '39': 'Ohio (OH)',
  '40': 'Oklahoma (OK)',
  '41': 'Oregon (OR)',
  '42': 'Pennsylvania (PA)',
  '44': 'Rhode Island (RI)',
  '45': 'South Carolina (SC)',
  '46': 'South Dakota (SD)',
  '47': 'Tennessee (TN)',
  '48': 'Texas (TX)',
  '49': 'Utah (UT)',
  '50': 'Vermont (VT)',
  '51': 'Virginia (VA)',
  '53': 'Washington (WA)',
  '54': 'West Virginia (WV)',
  '55': 'Wisconsin (WI)',
  '56': 'Wyoming (WY)',
};

const width = 800;
const height = 500;

interface MapDataPoint {
  state: string;
  count: number;
}

interface USMapProps {
  data: MapDataPoint[];
  totalCount?: number;
}

// Helper function to get color based on value
const getColorForValue = (value: number, maxValue: number): string => {
  if (value === 0) return '#1f2937'; // Default gray for no data

  // Calculate opacity based on value (0.2 to 1 range)
  const opacity = 0.4 + (value / maxValue) * 1;

  // Using the same base color (#67E8F9) but with varying opacity
  const r = 103;
  const g = 232;
  const b = 249;

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const USMap = ({ data = [], totalCount }: USMapProps) => {
  const geojson: any = feature(topology as any, topology.objects.states as any);
  const [statesData] = useState(geojson.features);

  interface TooltipData {
    x: number;
    y: number;
    state: string;
    count: number;
    percentage: string;
  }

  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const projection = geoAlbersUsa()
    .scale(1000)
    .translate([width / 2, height / 2]);

  const pathGenerator = geoPath().projection(projection);

  // Handlers to show/hide/update the tooltip.
  const handleMouseEnter = (e: React.MouseEvent<SVGPathElement, MouseEvent>, feature: any) => {
    if (!containerRef.current) return;
    const { left, top } = containerRef.current.getBoundingClientRect();
    const x = e.clientX - left;
    const y = e.clientY - top;
    const stateName = (fipsToName as any)[feature.id];

    const stateData = data.find((d) =>
      stateName?.toLocaleLowerCase().includes(d?.state?.toLocaleLowerCase()),
    );
    const count = stateData ? stateData.count : 0;
    const total = totalCount || data.reduce((sum, d) => sum + d.count, 0);
    const percentage = (count / total) * 100;
    // format percentage as a string with 1 decimal place
    setTooltip({ x, y, state: stateName, count, percentage: percentage.toFixed(1) });
  };

  const handleMouseMove = (e: React.MouseEvent<SVGPathElement, MouseEvent>) => {
    if (!containerRef.current || !tooltip) return;
    const { left, top } = containerRef.current.getBoundingClientRect();
    const x = e.clientX - left;
    const y = e.clientY - top;
    setTooltip((prev) => prev && { ...(prev as any), x, y });
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  // Calculate max value once when data changes
  const maxCount = useMemo(() => {
    return Math.max(...data.map((d) => d.count), 1); // Use 1 as fallback to avoid division by zero
  }, [data]);

  return (
    <div ref={containerRef} style={{ position: 'relative', width, height }}>
      <svg width={width} height={height}>
        {statesData.map((feature: any, i: number) => {
          const stateName = fipsToName[feature.id];
          const stateData = data.find((d) =>
            stateName?.toLocaleLowerCase().includes(d?.state?.toLocaleLowerCase()),
          );
          // const fillColor = stateData ? '#f7732a' : '#1f2937';

          const count = stateData ? stateData.count : 0;
          const fillColor = getColorForValue(count, maxCount);

          return (
            <path
              key={`state-${i}`}
              d={pathGenerator(feature) || ''}
              fill={fillColor}
              stroke="#f3f4f6"
              strokeWidth={0.5}
              onMouseEnter={(e) => handleMouseEnter(e, feature)}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            />
          );
        })}
      </svg>
      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            position: 'absolute',
            top: tooltip.y + 10,
            left: tooltip.x + 10,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          <div className="bg-neutral-900 border border-gray-700 shadow-xl p-4 rounded">
            <p className="text-gray-300">
              <strong>State: {tooltip.state}</strong>
            </p>
            <p className="font-semibold text-gray-300">Count: {tooltip.count}</p>
            <p className="font-semibold text-gray-300">Percentage: {tooltip.percentage}%</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default USMap;
